import { call, put, race, select, take, takeLatest } from "redux-saga/effects";
import { ActionType, getType } from "typesafe-actions";

import { Activity } from "@mapmycustomers/shared/types/entity";

import {
  createCoachingTask,
  CreateCoachingTaskRequest,
} from "@app/scene/dashboard/store/coaching/tasks/actions";
import {
  runCreateCoachingTask,
  uploadCoachingTaskFiles,
} from "@app/scene/dashboard/store/coaching/tasks/sagas";
import { handleError } from "@app/store/errors/actions";

import { createCoachingNote } from "../notes/actions";

import {
  createCoachingNoteWithTask,
  InlineTaskPayload,
  openTaskRetryModal,
  retryCoachingTask,
} from "./actions";
import { FailedTaskRetry } from "./reducers";
import { getCoachingNoteWithTaskFailedTask } from "./selectors";

const buildTaskRequest = (
  taskPayload: InlineTaskPayload,
  coachingNoteId: number
): CreateCoachingTaskRequest => ({
  assigneeId: taskPayload.assigneeId,
  coachingNoteId,
  crmActivityTypeId: taskPayload.crmActivityTypeId,
  dueDate: taskPayload.dueDate,
  fileIds: taskPayload.fileIds,
  mentions: taskPayload.mentions,
  name: taskPayload.name,
  newFiles: taskPayload.newFiles,
  note: taskPayload.note,
  subjectUserId: taskPayload.subjectUserId,
});

function* onCreateCoachingNoteWithTask({
  payload,
}: ReturnType<typeof createCoachingNoteWithTask.request>) {
  const { notePayload, taskPayload } = payload;

  yield put(createCoachingNote.request(notePayload));

  const noteOutcome: {
    err?: ActionType<typeof createCoachingNote.failure>;
    ok?: ActionType<typeof createCoachingNote.success>;
  } = yield race({
    err: take(
      (action: { payload?: { tempId?: number }; type: string }) =>
        action.type === getType(createCoachingNote.failure) &&
        action.payload?.tempId === notePayload.tempId
    ),
    ok: take(
      (action: { payload?: { tempId?: number }; type: string }) =>
        action.type === getType(createCoachingNote.success) &&
        action.payload?.tempId === notePayload.tempId
    ),
  });

  if (!noteOutcome.ok) {
    yield put(createCoachingNoteWithTask.failure({ stage: "note" }));
    return;
  }
  const note = noteOutcome.ok.payload.note;

  // Upload first so the retry stash can carry the uploaded fileIds and avoid
  // re-uploading on a task-POST retry.
  let finalRequest: CreateCoachingTaskRequest;
  try {
    finalRequest = yield call(uploadCoachingTaskFiles, buildTaskRequest(taskPayload, note.id));
  } catch (error) {
    yield put(createCoachingTask.failure());
    yield put(openTaskRetryModal({ noteId: note.id, taskPayload }));
    yield put(createCoachingNoteWithTask.failure({ stage: "task" }));
    // Retry modal is the user-facing surface; suppress the toast but still log.
    yield put(handleError({ displayNotification: false, error }));
    return;
  }

  try {
    const task: Activity = yield call(runCreateCoachingTask, finalRequest);
    // Mirror the standalone success dispatch so the tasks reducer prepends
    yield put(createCoachingTask.success(task));
    yield put(createCoachingNoteWithTask.success({ note, task }));
  } catch (error) {
    // Retry modal is the primary error surface for the orchestrator path —
    // stash the post-upload payload so retry won't re-upload.
    const retryPayload: InlineTaskPayload = {
      ...taskPayload,
      fileIds: finalRequest.fileIds,
      newFiles: undefined,
    };
    yield put(createCoachingTask.failure());
    yield put(openTaskRetryModal({ noteId: note.id, taskPayload: retryPayload }));
    yield put(createCoachingNoteWithTask.failure({ stage: "task" }));
    yield put(handleError({ displayNotification: false, error }));
  }
}

function* onRetryCoachingTask() {
  const failed: FailedTaskRetry | null = yield select(getCoachingNoteWithTaskFailedTask);
  if (!failed) {
    yield put(retryCoachingTask.failure());
    return;
  }

  try {
    // Upload-then-create — handles both retry paths uniformly:
    //   - Upload-failed initial run: stashed taskPayload still has newFiles,
    //     so uploadCoachingTaskFiles retries the upload.
    //   - POST-failed initial run: stashed payload had newFiles cleared after
    //     the successful upload, so uploadCoachingTaskFiles is a no-op.
    const finalRequest: CreateCoachingTaskRequest = yield call(
      uploadCoachingTaskFiles,
      buildTaskRequest(failed.taskPayload, failed.noteId)
    );
    const task: Activity = yield call(runCreateCoachingTask, finalRequest);
    yield put(createCoachingTask.success(task));
    yield put(retryCoachingTask.success(task));
  } catch (error) {
    // Retry modal stays visible (failedTask not cleared on .failure) as the
    // user-facing surface; suppress the toast but still log to Sentry.
    yield put(createCoachingTask.failure());
    yield put(retryCoachingTask.failure());
    yield put(handleError({ displayNotification: false, error }));
  }
}

export function* coachingNoteWithTaskSagas() {
  yield takeLatest(createCoachingNoteWithTask.request, onCreateCoachingNoteWithTask);
  yield takeLatest(retryCoachingTask.request, onRetryCoachingTask);
}
